import { useCallback, useEffect, useMemo, useState } from "react";
import { App, Button, Card, DatePicker, Form, Select, Space, Table, Tag, Typography } from "antd";
import {
  CONVERSION_EVENTS,
  CONVERSION_SOURCES,
  type ConversionRecord,
  type ConversionStatRow,
  type ConversionsResponse,
  type ConversionsStatsResponse,
} from "../../types/conversions";
import { ActiveSubscriptionSwitch } from "../../components/ActiveSubscriptionSwitch";
import { useSupabaseUsers } from "../../lib/useSupabaseUsers";

const { Title, Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";
const PAGE_SIZE = 100;

function adminHeaders(): Record<string, string> {
  const token = localStorage.getItem("access_token");
  const h: Record<string, string> = { "Content-Type": "application/json" };
  if (token) h["Authorization"] = `Bearer ${token}`;
  return h;
}

async function adminFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: { ...adminHeaders(), ...init?.headers },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Request failed: ${response.statusText}`);
  }
  return response.json() as Promise<T>;
}

function formatDate(value: string | undefined): string {
  if (!value) return "—";
  const normalized = value.replace(/(\.\d{3})\d+/, "$1");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("pl-PL");
}

function rowKey(record: ConversionRecord): string {
  return record._id ?? `${record.created_at ?? ""}-${record.event ?? ""}-${record.source ?? ""}`;
}

type Filters = {
  event?: string;
  source?: string;
  userId?: string;
  since?: string;
  until?: string;
  /** Tylko konwersje użytkowników z aktywną subskrypcją. */
  activeOnly?: boolean;
};

const DEFAULT_FILTERS: Filters = { activeOnly: true };

function buildQuery(filters: Filters, extra: Record<string, string>): string {
  const params = new URLSearchParams(extra);
  if (filters.event) params.set("event", filters.event);
  if (filters.source) params.set("source", filters.source);
  if (filters.userId) params.set("user_id", filters.userId);
  if (filters.since) params.set("since", filters.since);
  if (filters.until) params.set("until", filters.until);
  if (filters.activeOnly) params.set("active_only", "true");
  return params.toString();
}

const enumOptions = (values: readonly string[]) => values.map((v) => ({ value: v, label: v }));

export const ConversionsList = () => {
  const { notification } = App.useApp();

  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);

  // Picker reaguje od razu; lista i liczniki — po „Filtruj”, jak pozostałe filtry.
  const { users, loading: usersLoading } = useSupabaseUsers(!!filters.activeOnly, [filters.userId]);

  const [rows, setRows] = useState<ConversionRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [rowsLoading, setRowsLoading] = useState(false);

  const [stats, setStats] = useState<ConversionStatRow[]>([]);
  const [statsLoading, setStatsLoading] = useState(false);

  const eventOptions = useMemo(() => enumOptions(CONVERSION_EVENTS), []);
  const sourceOptions = useMemo(() => enumOptions(CONVERSION_SOURCES), []);

  const loadRows = useCallback(
    async (activeFilters: Filters, page: number) => {
      setRowsLoading(true);
      try {
        const offset = (page - 1) * PAGE_SIZE;
        const query = buildQuery(activeFilters, {
          limit: String(PAGE_SIZE),
          offset: String(offset),
        });
        const response = await adminFetch<ConversionsResponse>(
          `${API_URL}/api/v1/admin/conversions?${query}`,
        );
        setRows(response.data ?? []);
        setTotal(response.total ?? 0);
      } catch (error) {
        setRows([]);
        setTotal(0);
        notification.error({
          message: "Nie udało się pobrać konwersji",
          description: error instanceof Error ? error.message : "Wystąpił nieoczekiwany błąd.",
        });
      } finally {
        setRowsLoading(false);
      }
    },
    [notification],
  );

  const loadStats = useCallback(
    async (activeFilters: Filters) => {
      setStatsLoading(true);
      try {
        const query = buildQuery(activeFilters, {});
        const response = await adminFetch<ConversionsStatsResponse>(
          `${API_URL}/api/v1/admin/conversions/stats${query ? `?${query}` : ""}`,
        );
        setStats(response.data ?? []);
      } catch (error) {
        setStats([]);
        notification.error({
          message: "Nie udało się pobrać statystyk",
          description: error instanceof Error ? error.message : "Wystąpił nieoczekiwany błąd.",
        });
      } finally {
        setStatsLoading(false);
      }
    },
    [notification],
  );

  useEffect(() => {
    loadRows(filters, currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    loadRows(filters, 1);
    loadStats(filters);
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => {
    setCurrentPage(1);
    loadRows(filters, 1);
    loadStats(filters);
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setCurrentPage(1);
    loadRows(DEFAULT_FILTERS, 1);
    loadStats(DEFAULT_FILTERS);
  };

  const patch = (next: Partial<Filters>) => setFilters((prev) => ({ ...prev, ...next }));

  const statsTotal = useMemo(() => stats.reduce((sum, row) => sum + (row.count ?? 0), 0), [stats]);

  return (
    <Card>
      <Title level={4}>Konwersje</Title>
      <Paragraph type="secondary">
        Eventy trackingowe z kolekcji <Text code>conversions</Text> (warstwa marketingowa). Filtruj po
        typie, źródle, użytkowniku i zakresie dat. Liczniki poniżej pokazują liczbę eventów per źródło i dzień.
      </Paragraph>

      <Form layout="vertical">
        <Space wrap size={16} align="end">
          <Form.Item label="Event" style={{ minWidth: 200, marginBottom: 12 }}>
            <Select
              allowClear
              placeholder="Wszystkie"
              value={filters.event}
              onChange={(value?: string) => patch({ event: value })}
              options={eventOptions}
            />
          </Form.Item>
          <Form.Item label="Źródło" style={{ minWidth: 200, marginBottom: 12 }}>
            <Select
              allowClear
              placeholder="Wszystkie"
              value={filters.source}
              onChange={(value?: string) => patch({ source: value })}
              options={sourceOptions}
            />
          </Form.Item>
          <Form.Item label="Użytkownik" style={{ minWidth: 280, marginBottom: 12 }}>
            <Select
              showSearch
              allowClear
              placeholder="Wszyscy / anonimowi"
              loading={usersLoading}
              value={filters.userId}
              onChange={(value?: string) => patch({ userId: value })}
              optionFilterProp="label"
              options={users.map((u) => ({ value: u.id, label: u.email || u.id }))}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 12 }}>
            <div style={{ height: 32, display: "flex", alignItems: "center" }}>
              <ActiveSubscriptionSwitch
                checked={!!filters.activeOnly}
                onChange={(checked) => patch({ activeOnly: checked })}
              />
            </div>
          </Form.Item>
          <Form.Item label="Zakres dat" style={{ marginBottom: 12 }}>
            <RangePicker
              showTime
              onChange={(range) =>
                patch({
                  since: range?.[0]?.toISOString(),
                  until: range?.[1]?.toISOString(),
                })
              }
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 12 }}>
            <Space>
              <Button type="primary" onClick={applyFilters}>
                Filtruj
              </Button>
              <Button onClick={resetFilters}>Wyczyść</Button>
            </Space>
          </Form.Item>
        </Space>
      </Form>

      <Card
        size="small"
        type="inner"
        title={`Liczniki — źródło × dzień (razem: ${statsTotal})`}
        style={{ marginBottom: 24 }}
      >
        <Table<ConversionStatRow>
          rowKey={(row) => `${row.day}-${row.source}`}
          dataSource={stats}
          loading={statsLoading}
          size="small"
          pagination={false}
          scroll={{ y: 240 }}
        >
          <Table.Column<ConversionStatRow> dataIndex="day" title="Dzień" width={160} />
          <Table.Column<ConversionStatRow>
            dataIndex="source"
            title="Źródło"
            render={(value: string) => <Tag>{value || "—"}</Tag>}
          />
          <Table.Column<ConversionStatRow>
            dataIndex="count"
            title="Liczba"
            width={120}
            render={(value: number) => value ?? 0}
          />
        </Table>
      </Card>

      <Table<ConversionRecord>
        rowKey={rowKey}
        dataSource={rows}
        loading={rowsLoading}
        pagination={{
          current: currentPage,
          pageSize: PAGE_SIZE,
          total,
          showSizeChanger: false,
          onChange: (page) => setCurrentPage(page),
        }}
        scroll={{ x: true }}
      >
        <Table.Column<ConversionRecord>
          dataIndex="created_at"
          title="Data"
          width={200}
          render={(value: string | undefined) => formatDate(value)}
        />
        <Table.Column<ConversionRecord>
          dataIndex="event"
          title="Event"
          width={170}
          render={(value: string | undefined) => (value ? <Tag color="blue">{value}</Tag> : "—")}
        />
        <Table.Column<ConversionRecord>
          dataIndex="source"
          title="Źródło"
          width={160}
          render={(value: string | undefined) => (value ? <Tag>{value}</Tag> : "—")}
        />
        <Table.Column<ConversionRecord>
          dataIndex="user_id"
          title="Użytkownik"
          width={220}
          render={(value: string | undefined, record) =>
            value || record.anon_id || <Text type="secondary">anon</Text>
          }
        />
        <Table.Column<ConversionRecord>
          dataIndex="query"
          title="Zapytanie"
          render={(value: string | undefined) => value || "—"}
        />
        <Table.Column<ConversionRecord>
          dataIndex="auction_id"
          title="Aukcja"
          width={160}
          render={(value: string | undefined) => value || "—"}
        />
        <Table.Column<ConversionRecord>
          dataIndex="position"
          title="Pozycja"
          width={100}
          render={(value: number | undefined) => (value !== undefined ? value : "—")}
        />
      </Table>
    </Card>
  );
};
